import { cacheService } from './cache';
import type { PaymentResponse, SessionValidationResponse, ClientUnpaidOrdersResponse, ClientAcceptedOrdersResponse, AuthResponse, PendingCheckoutResponse, CancelOrderResponse, CancelBatchResponse, Menu, PublicMenuResponse } from '../types';

export const API_BASE = import.meta.env.VITE_API_BASE_URL || 'http://127.0.0.1:8000';

export const getFullImageUrl = (url: string | null | undefined) => {
  if (!url) return '';
  if (url.startsWith('http')) return url;
  // Ensure we don't double slash
  const cleanUrl = url.startsWith('/') ? url : `/${url}`;
  return `${API_BASE}${cleanUrl}`;
};


export class ApiClient {
  private sessionToken: string | null = null;
  private clientToken: string | null = null;
  private identifier: string | null = null;
  private onSessionClosedCallback: (() => void) | null = null;

  constructor() {
    // Restaurar sesión al inicializar
    this.restoreSession();
  }

  setOnSessionClosed(callback: () => void) {
    this.onSessionClosedCallback = callback;
  }

  setSessionToken(token: string | null) {
    this.sessionToken = token;
    // También guardarlo en sessionStorage como respaldo
    if (token) {
      sessionStorage.setItem('api-session-token', token);
    } else {
      sessionStorage.removeItem('api-session-token');
    }
  }

  getSessionToken() {
    return this.sessionToken;
  }

  setClientToken(token: string | null) {
    this.clientToken = token;
    if (token) {
      sessionStorage.setItem('api-client-token', token);
    } else {
      sessionStorage.removeItem('api-client-token');
    }
  }

  setIdentifier(identifier: string | null) {
    this.identifier = identifier;
    if (identifier) {
      sessionStorage.setItem('api-identifier', identifier);
    } else {
      sessionStorage.removeItem('api-identifier');
    }
  }

  async loginWithGoogle(idToken: string): Promise<AuthResponse> {
    const response = await this.request<AuthResponse>('/auth/google/login/', {
      method: 'POST',
      body: JSON.stringify({ id_token: idToken }),
    });
    return response;
  }

  async login(payload: any): Promise<AuthResponse> {
    const response = await this.request<AuthResponse>('/auth/login/', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
    return response;
  }

  async linkSession(sessionUuid: string) {
    return this.request('/auth/link-session/', {
      method: 'POST',
      body: JSON.stringify({ session_uuid: sessionUuid })
    });
  }

  // Método para restaurar el token si existe
  restoreSession() {
    const savedSessionToken = sessionStorage.getItem('api-session-token');
    const savedClientToken = sessionStorage.getItem('api-client-token');
    const savedIdentifier = sessionStorage.getItem('api-identifier');

    if (savedSessionToken) {
      this.sessionToken = savedSessionToken;
    }
    if (savedClientToken) {
      this.clientToken = savedClientToken;
    }
    if (savedIdentifier) {
      this.identifier = savedIdentifier;
    }
  }

  async request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
    const url = `${API_BASE}/api/v1${endpoint}`;

    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'ngrok-skip-browser-warning': 'true',
      ...(options.headers as Record<string, string>),
    };

    // Smart Token Selection based on endpoint
    // Client Token endpoints: /auth/me/, /addresses/, /auth/link-session/, /user/addresses
    const isClientEndpoint = endpoint.startsWith('/auth/me') ||
      endpoint.startsWith('/addresses') ||
      endpoint.startsWith('/user/') ||
      endpoint.startsWith('/auth/link-session');

    if (isClientEndpoint && this.clientToken) {
      headers['Authorization'] = `Bearer ${this.clientToken}`;
    } else if (this.sessionToken) {
      headers['Authorization'] = `Bearer ${this.sessionToken}`;
    }
    // If no token, no Authorization header is sent (guest/public)

    if (this.identifier) {
      headers['x-identifier'] = this.identifier;
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
        credentials: 'include',
      });

      if (!response.ok) {
        if (response.status === 401 || response.status === 403) {
          const errorText = await response.text();
          if (response.status === 403 && errorText.includes('Session closed')) {
            // Limpiar caché cuando la sesión se cierra
            cacheService.clear();

            if (this.onSessionClosedCallback) {
              this.onSessionClosedCallback();
            } else {
              // Redirect to session closed page
              window.location.href = '/session-closed';
            }
            throw new Error('SESSION_CLOSED');
          }
          throw new Error('UNAUTHORIZED');
        }
        const errorText = await response.text();
        throw new Error(`HTTP ${response.status}: ${response.statusText} - ${errorText}`);
      }

      if (response.status === 204) {
        return null as unknown as T;
      }

      // Get the raw response text first
      const responseText = await response.text();

      const contentType = response.headers.get('content-type');

      if (contentType && contentType.includes('application/json')) {
        try {
          const jsonResponse = JSON.parse(responseText);
          return jsonResponse;
        } catch (parseError) {
          throw new Error('Invalid JSON response from server');
        }
      } else {
        // Try to parse as JSON anyway, in case the content-type header is wrong
        try {
          const jsonResponse = JSON.parse(responseText);
          return jsonResponse;
        } catch (parseError) {
          throw new Error(`Server returned non-JSON response: ${responseText}`);
        }
      }
    } catch (error) {
      throw error;
    }
  }

  // Authentication
  async validateSession(token?: string) {
    try {
      const headers: Record<string, string> = {};
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
      }
      if (this.identifier) {
        headers['x-identifier'] = this.identifier;
      }
      return await this.request<SessionValidationResponse>('/validate-jwt', {
        method: 'GET',
        headers,
      });
    } catch (error) {
      console.error('Session validation failed:', error);
      throw error;
    }
  }

  // Native Auth
  async loginNative(email: string, password: string): Promise<AuthResponse> {
    const response = await this.request<AuthResponse>('/auth/login/', {
      method: 'POST',
      body: JSON.stringify({ email, password, provider: 'native' }),
    });
    return response;
  }

  async registerNative(data: { email: string; password: string; first_name: string; last_name: string }): Promise<AuthResponse> {
    const response = await this.request<AuthResponse>('/auth/register/', {
      method: 'POST',
      body: JSON.stringify({ ...data, provider: 'native' }),
    });
    return response;
  }

  async verifyClient(email: string, code: string): Promise<AuthResponse> {
    const response = await this.request<AuthResponse>('/auth/verify/', {
      method: 'POST',
      body: JSON.stringify({ email, code }),
    });
    return response;
  }

  async requestPasswordReset(email: string): Promise<{ message: string }> {
    return this.request('/auth/password-reset/request/', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  async confirmPasswordReset(data: { email: string; code: string; new_password: string }): Promise<{ message: string }> {
    return this.request('/auth/password-reset/confirm/', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  }

  async loginGuest(email: string): Promise<{
    token: string;
    client: {
      uuid: string;
      email: string;
      first_name: string;
      last_name: string;
      full_name: string;
      avatar_url: string | null;
    };
    message: string;
  }> {
    return this.request('/auth/guest/', {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  }

  // Client orders
  async getClientUnpaidOrders() {
    try {
      return this.request<ClientUnpaidOrdersResponse>('/orders/client-unpaid-orders');
    } catch (error) {
      console.error('Failed to get client unpaid orders:', error);
      throw error;
    }
  }

  async getClientAcceptedOrders() {
    try {
      return this.request<ClientAcceptedOrdersResponse>('/orders/client-accepted-orders');
    } catch (error) {
      console.error('Failed to get client accepted orders:', error);
      throw error;
    }
  }

  // Open sessions
  async getOpenSessions() {
    try {
      return this.request('/tables/open-sessions');
    } catch (error) {
      console.error('Failed to get open sessions:', error);
      throw error;
    }
  }

  // Tables
  async callWaiter(tableId: string): Promise<{ number: number; calling: boolean }> {
    return this.request(`/tables/call/${tableId}/`, {
      method: 'POST',
    });
  }

  async cancelWaiterCall(): Promise<void> {
    return this.request(`/call-cancel/`, {
      method: 'POST',
    });
  }

  async getUnpaidOrders(tableId: string) {
    return this.request(`/tables/${tableId}/unpaid-orders/`);
  }

  // Menu Categories
  async getMenuCategories() {
    const cached = cacheService.get('menu-categories');
    if (cached) {
      return cached;
    }

    const data = await this.request('/menu-categories/');
    cacheService.set('menu-categories', data);
    return data;
  }

  // Products
  async getProducts() {
    const cached = cacheService.get('products');
    if (cached) {
      return cached;
    }

    const data = await this.request('/products/');
    cacheService.set('products', data);
    return data;
  }

  // Menus
  async getMenus() {
    const cached = cacheService.get('menus');
    if (cached) {
      return cached;
    }

    const data = await this.request<Menu[]>('/menus/');
    cacheService.set('menus', data);
    return data;
  }

  async getFilteredMenus() {
    // We don't cache filtered menus as they depend on the current time/day
    // or arguably we could cache them for a short period, but let's keep it simple for now
    return this.request<Menu[]>('/menu/filtered/');
  }

  // Orders
  async getOrders() {
    return this.request('/orders/');
  }

  async createOrder(order: any) {
    return this.request('/orders/', {
      method: 'POST',
      body: JSON.stringify(order),
    });
  }

  async createBatchOrders(orders: any[]) {
    return this.request('/orders/batch/', {
      method: 'POST',
      body: JSON.stringify({ orders }),
    });
  }

  // Abandoned Orders
  async getPendingCheckout(): Promise<PendingCheckoutResponse> {
    return this.request<PendingCheckoutResponse>('/orders/pending-checkout/');
  }

  async cancelOrder(uuid: string): Promise<CancelOrderResponse> {
    return this.request<CancelOrderResponse>(`/orders/${uuid}/cancel/`, {
      method: 'PATCH',
    });
  }

  async cancelOrdersBatch(orderUuids: string[]): Promise<CancelBatchResponse> {
    return this.request<CancelBatchResponse>('/orders/cancel-batch/', {
      method: 'POST',
      body: JSON.stringify({ order_uuids: orderUuids }),
    });
  }

  // Payments
  async createPayment(payment: { method: string; amount: string }): Promise<PaymentResponse> {
    return this.request('/payments/', {
      method: 'POST',
      body: JSON.stringify(payment),
    });
  }

  // Offers
  async getOffers() {
    const cached = cacheService.get('offers');
    if (cached) {
      return cached;
    }

    const data = await this.request('/offers/');
    cacheService.set('offers', data);
    return data;
  }

  async getUpcomingOffers() {
    const cached = cacheService.get('upcoming-offers');
    if (cached) {
      return cached;
    }

    const data = await this.request('/offers/upcoming/');
    cacheService.set('upcoming-offers', data);
    return data;
  }

  async getConfig() {
    const cached = cacheService.get('config');
    if (cached) {
      return cached;
    }

    const data = await this.request('/config/');
    cacheService.set('config', data);
    return data;
  }

  async getPublicMenu(tenantUuid: string): Promise<PublicMenuResponse> {
    return this.request<PublicMenuResponse>(`/menus/public/${tenantUuid}/`);
  }

  // Notifications
  async registerPushToken(token: string) {
    return this.request('/notifications/register-token/', {
      method: 'POST',
      body: JSON.stringify({ token }),
    });
  }
}

export const apiClient = new ApiClient();